import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Pencil, Trash2, Star, Eye, EyeOff, X, Loader2, Search, Sparkles, Send, Boxes } from "lucide-react";
import AdminShell from "./AdminShell";
import { cjListProducts, cjCategories, cjPatchProduct, cjDeleteProduct, cjRewrite, cjSetStock, errMessage } from "../../lib/api";
import { eur } from "../../config";

const STATUS_LABEL = { draft: "Brouillon", published: "Publié", archived: "Archivé" };

function EditModal({ product, categories, onClose, onSaved }) {
  const [form, setForm] = useState({
    display_name: product.display_name || "",
    category: product.category || "",
    subcategory: product.subcategory || "",
    display_short_description: product.display_short_description || "",
    display_description: product.display_description || "",
    tags: (product.tags || []).join(", "),
    is_active: product.is_active,
    is_visible: product.is_visible,
    is_featured: product.is_featured,
    publication_status: product.publication_status || "draft",
  });
  const [busy, setBusy] = useState(false);
  const [rewriting, setRewriting] = useState(false);
  const setVal = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const generate = async () => {
    setRewriting(true);
    try {
      const res = await cjRewrite({ product_name: form.display_name, raw_description: form.display_description, category: form.category });
      setVal("display_description", res.description);
      toast.success("Description régénérée !");
    } catch (e) {
      toast.error(errMessage(e, "Génération IA indisponible"));
    }
    setRewriting(false);
  };

  const save = async () => {
    setBusy(true);
    try {
      await cjPatchProduct(product.id, {
        ...form,
        subcategory: form.subcategory || null,
        tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
      });
      toast.success("Produit mis à jour");
      onSaved();
    } catch (e) {
      toast.error(errMessage(e, "Échec de la mise à jour"));
    }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="edit-modal">
      <div className="bg-white w-full max-w-lg max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-display text-xl text-encre">Modifier le produit</h2>
          <button onClick={onClose} data-testid="edit-close"><X size={20} className="text-ardoise" /></button>
        </div>
        <div className="space-y-3">
          <input value={form.display_name} onChange={(e) => setVal("display_name", e.target.value)} className="w-full border border-dore/30 px-4 py-2.5 text-sm outline-none focus:border-dore" data-testid="edit-name" placeholder="Nom" />
          <div className="grid grid-cols-2 gap-3">
            <select value={form.category} onChange={(e) => setVal("category", e.target.value)} className="border border-dore/30 px-3 py-2.5 text-sm bg-white" data-testid="edit-category">
              {categories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <select value={form.publication_status} onChange={(e) => setVal("publication_status", e.target.value)} className="border border-dore/30 px-3 py-2.5 text-sm bg-white" data-testid="edit-status">
              <option value="draft">Brouillon</option>
              <option value="published">Publié</option>
              <option value="archived">Archivé</option>
            </select>
          </div>
          <input value={form.display_short_description} onChange={(e) => setVal("display_short_description", e.target.value)} className="w-full border border-dore/30 px-4 py-2.5 text-sm outline-none focus:border-dore" placeholder="Description courte" data-testid="edit-short-desc" />
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs uppercase tracking-wider text-ardoise/70">Description</span>
              <button type="button" onClick={generate} disabled={rewriting} className="flex items-center gap-1.5 text-xs text-dore hover:text-noir disabled:opacity-50" data-testid="edit-generate-btn">
                {rewriting ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />} {rewriting ? "Génération..." : "Générer avec l'IA"}
              </button>
            </div>
            <textarea value={form.display_description} onChange={(e) => setVal("display_description", e.target.value)} rows={5} className="w-full border border-dore/30 px-4 py-2.5 text-sm outline-none focus:border-dore" placeholder="Description" data-testid="edit-description" />
          </div>
          <input value={form.tags} onChange={(e) => setVal("tags", e.target.value)} className="w-full border border-dore/30 px-4 py-2.5 text-sm outline-none focus:border-dore" placeholder="Tags (virgules)" data-testid="edit-tags" />
          <div className="flex flex-wrap gap-4 text-sm text-encre">
            <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={form.is_active} onChange={(e) => setVal("is_active", e.target.checked)} data-testid="edit-active" /> Actif</label>
            <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={form.is_visible} onChange={(e) => setVal("is_visible", e.target.checked)} data-testid="edit-visible" /> Visible</label>
            <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={form.is_featured} onChange={(e) => setVal("is_featured", e.target.checked)} data-testid="edit-featured" /> Vedette</label>
          </div>
        </div>
        <div className="flex gap-3 mt-6">
          <button onClick={onClose} className="flex-1 border border-dore/30 py-3 text-xs uppercase tracking-widest">Annuler</button>
          <button onClick={save} disabled={busy} className="flex-1 bg-noir text-white py-3 text-xs uppercase tracking-widest disabled:opacity-50 flex items-center justify-center gap-2" data-testid="edit-save">
            {busy ? <Loader2 size={14} className="animate-spin" /> : null} Enregistrer
          </button>
        </div>
      </div>
    </div>
  );
}

export default function MyProducts() {
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [q, setQ] = useState("");
  const [catFilter, setCatFilter] = useState("");
  const [editing, setEditing] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (catFilter) params.category = catFilter;
      const res = await cjListProducts(params);
      setProducts(res.products); setTotal(res.total);
    } catch { toast.error("Chargement impossible"); }
    setLoading(false);
  }, [q, catFilter]);

  useEffect(() => { cjCategories().then((d) => setCategories(d.categories)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (p) => {
    try {
      await cjDeleteProduct(p.id);
      toast.success("Produit supprimé");
      setConfirmDel(null); load();
    } catch { toast.error("Suppression impossible"); }
  };

  const togglePublish = async (p) => {
    const next = p.publication_status === "published" ? "draft" : "published";
    if (next === "published" && !p.has_stock) { toast.error("Impossible de publier : aucune variante en stock"); return; }
    try {
      await cjPatchProduct(p.id, { publication_status: next });
      toast.success(next === "published" ? "Produit publié ✅" : "Produit dépublié");
      load();
    } catch (e) { toast.error(errMessage(e, "Action impossible")); }
  };

  const setStock = async (p) => {
    const val = window.prompt(`Stock à appliquer à toutes les variantes de « ${p.display_name} » :`, "5");
    if (val === null) return;
    const qty = parseInt(val, 10);
    if (Number.isNaN(qty) || qty < 0) { toast.error("Quantité invalide"); return; }
    try {
      await cjSetStock(p.id, qty);
      toast.success(`Stock mis à ${qty}`);
      load();
    } catch (e) { toast.error(errMessage(e, "Mise à jour du stock impossible")); }
  };

  return (
    <AdminShell title="Mes produits">
      <div className="flex flex-wrap gap-2 mb-5">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-ardoise/50" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher..." className="w-full border border-dore/30 pl-9 pr-4 py-2.5 text-sm outline-none focus:border-dore" data-testid="products-search" />
        </div>
        <select value={catFilter} onChange={(e) => setCatFilter(e.target.value)} className="border border-dore/30 px-3 py-2.5 text-sm bg-white" data-testid="products-cat-filter">
          <option value="">Toutes catégories</option>
          {categories.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      <p className="text-xs text-ardoise/70 mb-3" data-testid="products-count">{total} produit(s)</p>

      {loading ? (
        <p className="text-ardoise text-sm">Chargement...</p>
      ) : products.length === 0 ? (
        <p className="text-ardoise text-sm" data-testid="products-empty">Aucun produit importé. Rendez-vous sur le catalogue CJ pour en importer.</p>
      ) : (
        <div className="border border-dore/20 overflow-x-auto" data-testid="products-table">
          <table className="w-full text-sm min-w-[640px]">
            <thead>
              <tr className="bg-rose/10 text-[11px] uppercase tracking-wider text-ardoise/70 text-left">
                <th className="px-3 py-2.5">Produit</th>
                <th className="px-3 py-2.5">Catégorie</th>
                <th className="px-3 py-2.5">Prix</th>
                <th className="px-3 py-2.5">Stock</th>
                <th className="px-3 py-2.5">Statut</th>
                <th className="px-3 py-2.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {products.map((p) => (
                <tr key={p.id} className="border-t border-dore/10" data-testid="product-row">
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-2">
                      <div className="w-[60px] h-[60px] bg-rose/20 rounded overflow-hidden flex-shrink-0">
                        {p.main_image_url ? <img src={p.main_image_url} alt="" className="w-full h-full object-cover" loading="lazy" onError={(e) => { e.currentTarget.style.display = "none"; }} /> : null}
                      </div>
                      <div>
                        <p className="text-encre flex items-center gap-1">{p.display_name} {p.is_featured && <Star size={12} className="text-dore fill-dore" />}</p>
                        <p className="text-[11px] text-ardoise/50">{p.variants_count} variante(s)</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-2.5 text-encre">{p.category}</td>
                  <td className="px-3 py-2.5 text-dore">{p.price_min === p.price_max ? eur(p.price_min) : `${eur(p.price_min)} - ${eur(p.price_max)}`}</td>
                  <td className="px-3 py-2.5">{p.total_stock}</td>
                  <td className="px-3 py-2.5">
                    <span className="inline-flex items-center gap-1 text-[11px] bg-dore/15 text-encre px-2 py-0.5 uppercase tracking-wider">
                      {p.is_visible ? <Eye size={11} /> : <EyeOff size={11} />} {STATUS_LABEL[p.publication_status] || p.publication_status}
                    </span>
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center justify-end gap-1.5">
                      <button onClick={() => togglePublish(p)} className={`p-1.5 rounded ${p.publication_status === "published" ? "hover:bg-amber-50 text-amber-600" : "hover:bg-green-50 text-green-600"}`} data-testid="product-publish-btn" title={p.publication_status === "published" ? "Dépublier" : "Publier"}>
                        {p.publication_status === "published" ? <EyeOff size={15} /> : <Send size={15} />}
                      </button>
                      <button onClick={() => setStock(p)} className="p-1.5 hover:bg-dore/10 rounded text-encre" data-testid="product-stock-btn" title="Régler le stock"><Boxes size={15} /></button>
                      <button onClick={() => setEditing(p)} className="p-1.5 hover:bg-dore/10 rounded" data-testid="product-edit-btn" title="Modifier"><Pencil size={15} className="text-encre" /></button>
                      <button onClick={() => setConfirmDel(p)} className="p-1.5 hover:bg-red-50 rounded" data-testid="product-delete-btn" title="Supprimer"><Trash2 size={15} className="text-red-500" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && <EditModal product={editing} categories={categories} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}

      {confirmDel && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setConfirmDel(null)} data-testid="delete-modal">
          <div className="bg-white w-full max-w-sm p-6 text-center" onClick={(e) => e.stopPropagation()}>
            <Trash2 size={28} className="text-red-500 mx-auto mb-3" />
            <p className="text-encre mb-1">Supprimer « {confirmDel.display_name} » ?</p>
            <p className="text-xs text-ardoise/60 mb-5">Cette action supprime aussi ses variantes.</p>
            <div className="flex gap-3">
              <button onClick={() => setConfirmDel(null)} className="flex-1 border border-dore/30 py-2.5 text-xs uppercase tracking-widest">Annuler</button>
              <button onClick={() => remove(confirmDel)} className="flex-1 bg-red-500 text-white py-2.5 text-xs uppercase tracking-widest" data-testid="confirm-delete-btn">Supprimer</button>
            </div>
          </div>
        </div>
      )}
    </AdminShell>
  );
}
